import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	MenuItem,
	TextField,
} from "@mui/material";
import { MesfModal } from "@dexteel/mesf-core";
import {
	useBarrels,
	useStands,
	useUpsertRollset,
} from "../hooks/useRollshopOperations";

interface Props {
	open: boolean;
	onClose: () => void;
	onCreated: (id: number) => void;
}

// New Rollset modal — replicates the legacy `NewItemModal` flow:
//  - User picks Stand + Barrel + types a Name
//  - INSERT in catRollsets via UpsertRollsetProxy
//
// Barrel options are filtered by the chosen Stand to mirror the legacy
// `barrelsFiltered` Backbone computed.
export const NewRollsetModal = ({ open, onClose, onCreated }: Props) => {
	const standsQuery = useStands();
	const barrelsQuery = useBarrels();

	const [name, setName] = useState("");
	const [standId, setStandId] = useState<number | null>(null);
	const [barrelId, setBarrelId] = useState<number | null>(null);
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (open) {
			setName("");
			setStandId(null);
			setBarrelId(null);
			setErrorMsg("");
		}
	}, [open]);

	const filteredBarrels = useMemo(() => {
		const all = barrelsQuery.data ?? [];
		if (standId == null) return all;
		// Some barrel rows may not carry StandId; in that case we surface them
		// all rather than hiding them — legacy behaviour was to filter, but
		// we want the modal usable even on incomplete catalog data.
		return all.filter((b) => {
			const bs = (b as unknown as { StandId?: number | null }).StandId;
			return bs == null || bs === standId;
		});
	}, [barrelsQuery.data, standId]);

	const { upsertRollset, isUpserting } = useUpsertRollset({
		onSuccess: (data) => {
			if (data?.Id) onCreated(data.Id);
			onClose();
		},
		onError: (e) => setErrorMsg(e.message),
	});

	const millSizeId = useMemo(() => {
		if (standId == null) return null;
		const s = (standsQuery.data ?? []).find((x) => x.Id === standId);
		return s?.MillSizeId ?? null;
	}, [standId, standsQuery.data]);

	const canSave = name.trim().length > 0 && standId != null && !isUpserting;

	const handleSave = () => {
		setErrorMsg("");
		upsertRollset({
			Id: null,
			Name: name.trim(),
			StandId: standId,
			MillSizeId: millSizeId,
			BarrelId: barrelId,
			StatusCode: "AVAILABLE",
			Comments: null,
		});
	};

	return (
		<MesfModal
			title="NEW ROLLSET"
			open={open}
			handleClose={onClose}
			id="new-rollset-modal"
			maxWidth="sm"
		>
			<MesfModal.Content style={{ padding: "20px 30px" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<TextField
						size="small"
						label="Name *"
						value={name}
						onChange={(e) => setName(e.target.value)}
						inputProps={{ maxLength: 20 }}
						autoFocus
					/>
					<TextField
						select
						size="small"
						label="Stand *"
						value={standId == null ? "" : String(standId)}
						onChange={(e) => {
							setStandId(e.target.value === "" ? null : Number(e.target.value));
							setBarrelId(null);
						}}
					>
						<MenuItem value="">—</MenuItem>
						{(standsQuery.data ?? []).map((s) => (
							<MenuItem key={s.Id} value={String(s.Id)}>
								{s.Name}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						size="small"
						label="Barrel"
						value={barrelId == null ? "" : String(barrelId)}
						onChange={(e) =>
							setBarrelId(e.target.value === "" ? null : Number(e.target.value))
						}
						disabled={standId == null || filteredBarrels.length === 0}
					>
						<MenuItem value="">—</MenuItem>
						{filteredBarrels.map((b) => (
							<MenuItem key={b.Id} value={String(b.Id)}>
								{b.Name}
							</MenuItem>
						))}
					</TextField>
					{errorMsg && (
						<Box sx={{ color: "error.main", fontSize: 13 }}>{errorMsg}</Box>
					)}
				</Box>
			</MesfModal.Content>
			<MesfModal.Actions style={{ padding: "15px 30px 20px" }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 1,
						width: "100%",
					}}
				>
					<Button
						variant="text"
						color="inherit"
						onClick={onClose}
						disabled={isUpserting}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						disabled={!canSave}
						startIcon={
							isUpserting && <CircularProgress size={16} color="inherit" />
						}
					>
						{isUpserting ? "SAVING..." : "CREATE"}
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
